import { dryrun } from "../api/ao-connection"
import { useQuery } from "@tanstack/react-query"

import type { Tag } from "@/types"
import ENV from "@/env"
import { getHBBaseUrl } from "@/utils/hb-router"

async function getCommunityApprovedHB(token: string): Promise<boolean> {
  const HB_API = getHBBaseUrl(ENV.VITE_DEXI_PROCESS as string)

  const response = await fetch(
    `${HB_API}/${ENV.VITE_DEXI_PROCESS}~process@1.0/compute/community_approved_tokens`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  )

  if (!response.ok) {
    throw new Error("Failed to fetch community approved tokens")
  }

  const data = (await response.json()) as string[]

  // Check if token is in the list of approved tokens
  const approved = data.includes(token)

  return approved
}

async function getCommunityApprovedDryrun(token: string): Promise<boolean> {
  const result = await dryrun({
    process: ENV.VITE_DEXI_PROCESS,
    tags: [{ name: "Action", value: "Get-Community-Approved-Tokens" }],
  })

  if (result.Messages.length === 0)
    throw new Error("No response from Get-Community-Approved-Tokens")

  const tags = result.Messages[0].Tags

  // check for Community-Approved-Tokens tag name and return it's values
  const data = tags.find((tag: Tag) => tag.name === "Community-Approved-Tokens")?.value as string[]

  if (!data) throw new Error("Response malformed")

  // Check if token is in the list of approved tokens
  const approved = data.includes(token)

  return approved
}

async function getCommunityApproved(token: string): Promise<boolean> {
  try {
    const response = await getCommunityApprovedHB(token)

    return response
  } catch (error) {
    console.error("Error fetching community approved tokens from HB:", error)

    // Fallback to dryrun if HB fails
    try {
      const response = await getCommunityApprovedDryrun(token)
      return response
    } catch (error) {
      console.error("Error fetching community approved tokens from dryrun:", error)
      throw new Error("Failed to fetch community approved tokens from both sources")
    }
  }
}

const useCommunityApproved = (token: string | undefined) => {
  return useQuery({
    queryKey: ["Get-Community-Approved-Tokens", token],
    queryFn: () => getCommunityApproved(token as string),
    enabled: !!token,
  })
}

export default useCommunityApproved
